import { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Eraser, ShieldCheck } from "lucide-react";

export default function SignatureModal({ open, onOpenChange, onboardingId, task, defaultName = "", signerEmail = "", onSigned }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [signerName, setSignerName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [dsStatus, setDsStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/docusign/status").then(r => setDsStatus(r.data)).catch(() => setDsStatus(null));
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0b0b0b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    setHasDrawn(false);
    setSignerName(defaultName);
  }, [open, defaultName]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - rect.left) * (c.width / rect.width), y: (p.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); setDrawing(true); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing) return; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setHasDrawn(true); };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "white"; ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (!hasDrawn || !signerName.trim()) { toast.error("Please sign and enter your name"); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const { data } = await api.post(`/onboarding/${onboardingId}/tasks/${task.task_id}/sign`, {
        signature_data_url: dataUrl,
        signer_name: signerName.trim(),
      });
      toast.success("Document signed");
      onOpenChange(false);
      onSigned && onSigned(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Sign failed"); }
    finally { setBusy(false); }
  };

  const sendDocuSign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/docusign/envelope`, {
        onboarding_id: onboardingId,
        task_id: task.task_id,
        signer_name: signerName.trim() || defaultName,
        signer_email: signerEmail,
        document_title: task?.title || "Employment Contract",
      });
      toast.success(`DocuSign envelope sent (${data.envelope_id})`);
      onOpenChange(false);
      onSigned && onSigned(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "DocuSign failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="signature-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Sign: {task?.title}
            {dsStatus?.configured && <Badge className="bg-teal text-white border-0 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> DocuSign</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Full legal name</Label>
            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1.5" data-testid="signer-name-input" />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Label>Draw your signature</Label>
              <button onClick={clear} className="text-xs text-neutral-500 hover:text-pink flex items-center gap-1" data-testid="clear-sig-btn">
                <Eraser className="w-3 h-3" /> Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={640}
              height={220}
              className="mt-1.5 w-full border rounded-lg bg-white touch-none"
              style={{ cursor: "crosshair" }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="signature-canvas"
            />
            <p className="text-xs text-neutral-500 mt-1">By signing, you agree this constitutes a legally binding electronic signature.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          {dsStatus?.configured && (
            <Button variant="outline" onClick={sendDocuSign} disabled={busy} data-testid="send-docusign-btn">
              <ShieldCheck className="w-4 h-4 mr-1" /> {busy ? "Sending…" : "Send DocuSign envelope"}
            </Button>
          )}
          <Button className="btn-primary" onClick={submit} disabled={busy || !hasDrawn} data-testid="submit-signature-btn">
            {busy ? "Signing…" : "Adopt and Sign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
